package com.crowpay.views.components.project.work.payApps

import com.crowpay.Formats
import com.crowpay.HasDisplayName
import com.crowpay.PayApplication
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.format
import com.crowpay.sdk.notFoundError
import com.crowpay.sdk.notNullSession
import com.crowpay.utils.SignalingList
import com.crowpay.utils.containsDynamic
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.renderDollars
import com.crowpay.views.components.label2
import com.crowpay.views.components.labeledSection
import com.crowpay.views.dialogs.DrawWaitedDetails
import com.crowpay.views.dialogs.GenericConfirmationDialog
import com.crowpay.views.dialogs.WaitDrawsDialog
import com.crowpay.views.screens.common.AuthorizedProjectView
import com.crowpay.views.screens.common.ProjectLens
import com.crowpay.views.screens.common.assertViewerLens
import com.crowpay.views.screens.contractor.MightBeContractorPage
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.ApprovedSemantic
import com.crowpay.views.theming.DangerButtonSemantic
import com.crowpay.views.theming.DarkSection
import com.crowpay.views.theming.DeniedSemantic
import com.crowpay.views.theming.DisplayableStateSemantic
import com.crowpay.views.theming.LightSection
import com.crowpay.views.theming.ReviewSelectionSemantic
import com.crowpay.views.theming.TabSemantics
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.dangerButton
import com.crowpay.views.theming.darkSection
import com.crowpay.views.theming.greySeparator
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.primaryButton
import com.crowpay.views.theming.secondaryButton
import com.crowpay.views.theming.specCenteredText
import com.crowpay.views.theming.title
import com.lightningkite.UUID
import com.lightningkite.kiteui.AppScope
import com.lightningkite.kiteui.Routable
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.CardSemantic
import com.lightningkite.kiteui.models.ScreenTransition
import com.lightningkite.kiteui.models.div
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.bind
import com.lightningkite.kiteui.reactive.equalTo
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewModifiable
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.compact
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.checkbox
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.gravity
import com.lightningkite.kiteui.views.direct.link
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.radioToggleButton
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.scrolls
import com.lightningkite.kiteui.views.direct.stack
import com.lightningkite.kiteui.views.direct.swapView
import com.lightningkite.kiteui.views.direct.swapping
import com.lightningkite.kiteui.views.direct.toggleButton
import com.lightningkite.kiteui.views.dynamicTheme
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.forEachUpdating
import kotlinx.coroutines.launch
import kotlin.math.roundToLong

@Routable("projects/{projectId}/draws/{payAppId}/{lens}")
class PayAppDetails(val projectId: UUID, val payAppId: UUID, override val lens: ProjectLens) : MightBeContractorPage {
    val payApp = shared {
        notNullSession().payApplications[payAppId]() ?: notFoundError<PayApplication>()
    }

    override val title: Readable<String> = shared { "Draw ${payApp().number}" }

    val data = AuthorizedProjectView.Data(lens, projectId)

    val paymentData = shared { getPaymentData(data, payApp()) }

    val status = shared { paymentData().mapNotNull { it.payApp?.status }.toSet().overallStatus() }

    enum class SummarySection(override val displayName: String) : HasDisplayName {
        Requested("Requested"), Actual("Actual")
    }

    val selectedSummarySection = Property(SummarySection.Requested)

    val selected = SignalingList<UUID>()

    override fun ViewWriter.renderMainContent(): ViewModifiable = col {
        launch {
            data.assertViewerLens(lens)
        }

        expanding - scrolls - col {
            spacing = AppDimensions.majorColumnSpacing

            col {
                lightSection - col {
                    row {
                        centered - title { ::content { this@PayAppDetails.title() } }
                        centered - expanding - body { ::content { payApp().submitted?.format(Formats.mmddyyyy) ?: "" } }
                        centered - secondaryButton - link {
                            specCenteredText("View Details")
                            ::to { { PayAppSummary(payApp(), data.project(), data.scopes(), paymentData()) } }
                        }
                    }

                    label2("Status") {
                        title {
                            dynamicTheme {
                                DisplayableStateSemantic(status())
                            }
                            ::content { status().displayName }
                        }
                    }
                }

                labeledSection("Summary") {
                    TabSemantics(AppColors.grey.light2).onNext - row {
                        for (section in SummarySection.entries) {
                            expanding - radioToggleButton {
                                centered - body(section.displayName)
                                checked bind selectedSummarySection.equalTo(section)
                            }
                        }
                    }

                    swapView {
                        swapping(
                            current = { selectedSummarySection() },
                            transition = {
                                if (it == SummarySection.Requested) ScreenTransition.Pop else ScreenTransition.Push
                            },
                            views = {
                                when (it) {
                                    SummarySection.Requested -> requestedSummary()
                                    SummarySection.Actual -> actualsSummary()
                                }
                            }
                        )
                    }
                }
            }

            val pending = shared { paymentData().filter { it.payApp?.pending == true } }

            onlyWhen(false) { pending().isNotEmpty() } - label2("Pending Items") {
                ReviewSelectionSemantic.onNext - col {
                    spacing = AppDimensions.buttonSpacing
                    forEachUpdating(pending) {
                        lightSection - toggleButton {
                            enabled = lens.isClient()

                            val isSelected = selected.containsDynamic { it().lineItem._id }

                            if (lens.isClient()) row {
                                centered - checkbox {
                                    checked bind isSelected
                                }
                                expanding - itemSummary(it)
                            }
                            else itemSummary(it)

                            checked bind isSelected
                        }
                    }
                }
            }

            val reviewed = shared { paymentData().filter { it.payApp?.pending == false } }

            onlyWhen { reviewed().isNotEmpty() } - label2("Reviewed Items") {
                col {
                    spacing = AppDimensions.buttonSpacing
                    forEachUpdating(reviewed) { item ->
                        link {
                            dynamicTheme {
                                when (item().payApp?.individualStatus) {
                                    PayAppItemStatus.Pending -> LightSection
                                    PayAppItemStatus.Denied -> DeniedSemantic + CardSemantic
                                    PayAppItemStatus.Approved -> ApprovedSemantic + CardSemantic
                                    PayAppItemStatus.Voided -> DarkSection
                                    null -> LightSection
                                }
                            }
                            itemSummary(item)

                            ::to {
                                item().payApp?.denied?.let {
                                    val drawData = paymentData().filter { it.payApp != null };
                                    { DrawWaitedDetails(payApp(), drawData.mapNotNull { it.payApp }, drawData.associate { it.lineItem._id to it.lineItem }, data.client().name, data.contractor().name) }
                                }
                            }
                        }
                    }
                }
            }
        }

        darkSection - stack {
            spacing = AppDimensions.sectionIndent/2
            gravity(Align.End, Align.Center) - actionBar()
        }
    }

    private fun ViewWriter.entry(key: String, value: ReactiveContext.() -> Long) = row {
        centered - bodyBold(key)
        expanding - centered - body {
            align = Align.End
            ::content { value().renderDollars() }
        }
    }

    private fun ViewWriter.requestedSummary() = compact - col {
        val workValue = shared { paymentData().sumOf { it.payApp?.amount ?: 0 } }
        entry("Work Value") { workValue() }
        entry("Less Retention") { workValue().times(data.project().safeRetention).roundToLong() }
        greySeparator()
        entry("Requested") {
            workValue().let { it - (it * data.project().safeRetention).roundToLong() }
        }
    }
    private fun ViewWriter.actualsSummary() = compact - col {
        val approved = shared {
            paymentData()
                .mapNotNull { it.payApp?.takeIf { p -> p.approved != null } }
                .sumOf { it.amount }
        }

        entry("Approved") { approved() }
        entry("Less Retention") { approved().times(data.project().safeRetention).roundToLong() }
        greySeparator()
        entry("Paid") {
            approved().let { it - (it * data.project().safeRetention).roundToLong() }
        }
    }

    private fun ViewWriter.itemSummary(item: Readable<LineItemWithPaymentInfo>) = compact - col {
        fun ViewWriter.smallEntry(key: String, value: ReactiveContext.() -> Long) = row {
            centered - body(key)
            expanding - centered - body {
                align = Align.End
                ::content { value().renderDollars() }
            }
        }

        row {
            expanding - centered - bodyBold { ::content { item().name } }
            centered - body {
                dynamicTheme { item().payApp?.individualStatus?.let(::DisplayableStateSemantic) }
                ::content { item().payApp?.individualStatus?.displayName ?: "" }
            }
        }

        smallEntry("This Draw") { item().thisPayment ?: 0 }
        smallEntry("Less Retention") { item().thisPayment?.times(data.project().safeRetention)?.roundToLong() ?: 0 }
        greySeparator()
        smallEntry("Requested") { item().thisPayment?.let { it - (it * data.project().safeRetention).roundToLong() } ?: 0 }
//        space(0.5)
//
//        row {
//            expanding - centered - body("Work Completed")
//            compact - row {
//                centered - body { ::content { item().workCompleted.renderDollars() } }
//                centered - smallBody { ::content { "(${item().percentComplete}%)" } }
//            }
//        }
    }

    private fun ViewWriter.actionBar() = row {
        spacing = AppDimensions.buttonRowSpacing
        val pending = shared { paymentData().filter { it.payApp?.pending == true } }

        val allSelected = shared { pending().all { selected().contains(it.lineItem._id) } && pending().isNotEmpty() }
        val labelValue = shared {
            if (allSelected()) " All"
            else if (selected().size > 1 && selected().size < pending().size) " (${selected().size})"
            else ""
        }

        val canVoid = shared { lens == ProjectLens.Contractor && pending().isNotEmpty() && pending().all { it.payApp?.status == PayAppStatus.Pending } }
        val canPay = shared { lens == ProjectLens.Customer && pending().isNotEmpty() && selected().isNotEmpty() }
        val canWait = shared { lens == ProjectLens.Customer && pending().isNotEmpty() && selected().isNotEmpty() }

        if (lens == ProjectLens.Contractor) DangerButtonSemantic.onNext - button {
            existsDefaultFalse { canVoid() }
            specCenteredText("Void")
            onClick("Void Draw") {
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = "Void Draw",
                        message = "Are you sure you want to void the remaining draw items in this draw? This cannot be undone.",
                        confirmationText = "Void",
                        confirmationTheme = DangerButtonSemantic,
                        onSubmit = { confirmed ->
                            if (confirmed) {
                                val payItems = pending()
                                val session = notNullSession()
                                val endpoints = session.nonCached.payAppItem
                                AppScope.launch {
                                    payItems
                                        .mapNotNull { it.payApp }
                                        .filter { it.status == PayAppStatus.Pending }
                                        .forEach {
                                            val result = endpoints.voidPayApp(it._id)
                                            session.payAppItems.localInsert(result)
                                        }
                                }
                            }
                        }
                    )
                )
            }
        }
        if (lens == ProjectLens.Customer) primaryButton - button {
            existsDefaultFalse { canPay() }
            specCenteredText { "Pay${labelValue()}" }
            onClick("Pay Selected Draws") {
                val selectedPending = pending().filter { selected.contains(it.lineItem._id) }
                val total = selectedPending.sumOf { it.thisPayment ?: 0 }
                val retention = (total * data.project().safeRetention).roundToLong()
                val totalMinusRetention = (total - retention).renderDollars()
                dialogScreenNavigator.navigate(
                    GenericConfirmationDialog(
                        header = if (allSelected()) "Pay Draw" else "Pay ${selectedPending.size} Draw Items",
                        message = "Pay $totalMinusRetention out of escrow to contractor?",
                        confirmationText = "Pay $totalMinusRetention",
                        onSubmit = { confirmed ->
                            if (confirmed) {
                                val payItems = selectedPending.mapNotNull { it.payApp?._id }
                                notNullSession().nonCached.payAppItem.approvePayApps(payItems)
                                selected.clear()
                            }
                        }
                    )
                )
            }
        }
        if (lens == ProjectLens.Customer) dangerButton - button {
            existsDefaultFalse { canWait() }
            specCenteredText { "Wait${labelValue()}" }
            onClick("Wait Selected Draws") {
                val selectedPending = pending().filter { selected.contains(it.lineItem._id) }
                dialogScreenNavigator.navigate(
                    WaitDrawsDialog(
                        draws = selectedPending.toList(),
                        header = if (allSelected()) "Wait Draw?" else "Wait ${selectedPending.size} Draw Items",
                    ) { value ->
                        selected.remove(value)
                    }
                )
            }
        }
        if (lens == ProjectLens.Contractor) secondaryButton - button {
            ::exists { paymentData().any { it.payApp?.denied != null } }
            specCenteredText("View Concerns")
            onClick {
                val items = paymentData()
                dialogScreenNavigator.navigate(
                    DrawWaitedDetails(
                        payApp(),
                        items.mapNotNull { it.payApp },
                        items.associate { it.lineItem._id to it.lineItem },
                        data.client().name,
                        data.contractor().name
                    )
                )
            }
        }
    }
}